/**
 * Lifecycle reducer for the shared Table/Pitch watchlist preference order.
 *
 * Moved out of `watchlistPreferenceOrderState.ts` (SE-8389) so the save
 * semantics of PV23 · D5 have room beside the projection helpers: a failed
 * save keeps the change on screen and offers Retry (three strikes offer
 * Discard); a stale-version rejection adopts the other editor's version and
 * keeps the user's change as a copy to re-apply once — never silently retried
 * over their work (PRD D17).
 */
import {
  createPreferenceOrderState,
  projectPitchFormerSlots,
  type WatchlistPitchOperation,
  type WatchlistPreferenceOrderState,
  type WatchlistPreferenceSnapshot,
} from "./watchlistPreferenceOrderState";

/** Toolbar copy for a save that failed and is kept on screen. */
export const SAVE_FAILED_MESSAGE = "Couldn't save · Retry";

/** Toolbar copy for a stale-version rejection (no editor field yet). */
export const SAVE_CONFLICT_MESSAGE = "Updated by another editor · Reload";

/** Fallback when the backend refuses a re-apply without a readable message. */
export const REAPPLY_REFUSED_FALLBACK = "the server refused the change";

/** Consecutive failures of one operation after which Discard is offered. */
export const FAILURE_BANNER_THRESHOLD = 3;

/** Controller actions used by the async mutation hook. */
export type WatchlistPreferenceOrderAction =
  | {
      readonly type: "hydrate";
      readonly watchlistId: string;
      readonly snapshot: WatchlistPreferenceSnapshot;
    }
  | {
      readonly type: "start";
      readonly watchlistId: string;
      readonly operation: WatchlistPitchOperation;
    }
  | {
      readonly type: "success";
      readonly watchlistId: string;
      readonly operationId: string;
      readonly snapshot: WatchlistPreferenceSnapshot;
      /** When the save landed; the hook passes the wall clock. */
      readonly savedAt?: number;
    }
  | {
      readonly type: "failure";
      readonly watchlistId: string;
      readonly operationId: string;
      /** The backend's message, read verbatim when a re-apply is refused. */
      readonly message?: string;
    }
  | {
      readonly type: "conflict";
      readonly watchlistId: string;
      readonly operationId: string;
      readonly snapshot: WatchlistPreferenceSnapshot;
    }
  | {
      /** Re-sends one failed operation; the hook owns the request. */
      readonly type: "retry";
      readonly watchlistId: string;
      readonly operationId: string;
    }
  | {
      /** Rolls back one failed operation only. */
      readonly type: "discard";
      readonly watchlistId: string;
      readonly operationId: string;
    }
  | {
      /** "Reload their version": drops the held copy. */
      readonly type: "reload";
      readonly watchlistId: string;
    }
  | {
      /** "Re-apply my change": queues the held copy once more. */
      readonly type: "reapply";
      readonly watchlistId: string;
    };

/** One member of the action union, by its `type`. */
type ActionOf<Type extends WatchlistPreferenceOrderAction["type"]> = Extract<
  WatchlistPreferenceOrderAction,
  { readonly type: Type }
>;

const shouldIgnoreHydration = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"hydrate">
): boolean =>
  state.watchlistId === action.watchlistId &&
  (state.operations.length > 0 ||
    state.confirmed.pitchRevision > action.snapshot.pitchRevision ||
    (state.confirmed.pitchRevision === action.snapshot.pitchRevision &&
      state.confirmed.orderedPlayerIds.length >=
        action.snapshot.orderedPlayerIds.length));

/**
 * What a newer snapshot of the SAME watchlist must not erase: a held copy is
 * relative intent that replays over any order, and a re-apply refusal is an
 * answer the user has not dismissed yet (D17 — the server never throws the
 * user's change away silently, so neither does a refetch).
 * @param state - Current shared ordering state
 * @returns The recovery fields to carry across the hydration
 */
const recoveryToKeep = (
  state: WatchlistPreferenceOrderState
): Partial<WatchlistPreferenceOrderState> =>
  state.heldChange || state.reapplyRefusal
    ? {
        heldChange: state.heldChange,
        reapplyRefusal: state.reapplyRefusal,
        status: state.status,
        message: state.message,
      }
    : {};

const reduceHydration = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"hydrate">
): WatchlistPreferenceOrderState => {
  if (shouldIgnoreHydration(state, action)) return state;
  const sameList = state.watchlistId === action.watchlistId;
  return {
    ...createPreferenceOrderState(action.watchlistId),
    confirmed: action.snapshot,
    ...(sameList ? recoveryToKeep(state) : {}),
    // SE-8391: the former slots are client memory the server cannot
    // return; a newer read of the SAME list keeps them.
    ...(sameList && state.formerSlotByPlayerId
      ? { formerSlotByPlayerId: state.formerSlotByPlayerId }
      : {}),
  };
};

/**
 * Folds the former slots of every operation a server snapshot supersedes
 * into the state, so they outlive the operations' removal (SE-8391).
 * @param state - Current shared ordering state
 * @param supersededCount - How many queued operations the snapshot settles
 * @returns The former slots to keep on the next state
 */
const settledFormerSlots = (
  state: WatchlistPreferenceOrderState,
  supersededCount: number
): Readonly<Record<string, string>> =>
  projectPitchFormerSlots({
    ...state,
    operations: state.operations.slice(0, supersededCount),
  });

/**
 * Position of one operation in the queue.
 * @param state - Current shared ordering state
 * @param operationId - Operation identity
 * @returns Index in the queue, or -1
 */
const findOperationIndex = (
  state: WatchlistPreferenceOrderState,
  operationId: string
): number =>
  state.operations.findIndex(operation => operation.id === operationId);

/**
 * A refused re-apply: the copy is discarded, the backend message is named and
 * nothing is retried (D17).
 * @param state - Current shared ordering state
 * @param action - The failure of the re-applied operation
 * @returns Next state with the copy gone
 */
const reduceReapplyRefusal = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"failure">
): WatchlistPreferenceOrderState => {
  const refusal = action.message || REAPPLY_REFUSED_FALLBACK;
  return {
    ...state,
    operations: state.operations.filter(
      operation => operation.id !== action.operationId
    ),
    reapplyingOperationId: undefined,
    reapplyRefusal: refusal,
    status: "error",
    message: `Couldn't re-apply: ${refusal}`,
  };
};

/**
 * A failed save keeps its projected change and counts the strike.
 * @param state - Current shared ordering state
 * @param action - The failure event
 * @returns Next state, the change still projected
 */
const reduceFailure = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"failure">
): WatchlistPreferenceOrderState => {
  if (findOperationIndex(state, action.operationId) < 0) return state;
  if (state.reapplyingOperationId === action.operationId) {
    return reduceReapplyRefusal(state, action);
  }
  const attempts =
    state.failure?.operationId === action.operationId
      ? state.failure.attempts + 1
      : 1;
  return {
    ...state,
    status: "error",
    message: SAVE_FAILED_MESSAGE,
    failure: { operationId: action.operationId, attempts },
  };
};

/**
 * The remote snapshot wins; the rejected operation becomes the held copy.
 * @param state - Current shared ordering state
 * @param action - The conflict event and the refetched snapshot
 * @returns Next state on the server version
 */
const reduceConflict = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"conflict">
): WatchlistPreferenceOrderState => {
  const conflictedIndex = findOperationIndex(state, action.operationId);
  if (conflictedIndex < 0) return state;
  return {
    ...state,
    confirmed: action.snapshot,
    // The remote snapshot supersedes the conflicting operation and older
    // requests. Later local intent remains queued and replays over it.
    operations: state.operations.slice(conflictedIndex + 1),
    formerSlotByPlayerId: settledFormerSlots(state, conflictedIndex),
    status: "conflict",
    message: SAVE_CONFLICT_MESSAGE,
    heldChange: state.operations[conflictedIndex],
    reapplyingOperationId: undefined,
    reapplyRefusal: undefined,
    failure: undefined,
  };
};

/**
 * A server snapshot supersedes the operation and everything older.
 * @param state - Current shared ordering state
 * @param action - The success event and its snapshot
 * @returns Next state
 */
const reduceSuccess = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"success">
): WatchlistPreferenceOrderState => {
  const succeededIndex = findOperationIndex(state, action.operationId);
  if (succeededIndex < 0) return state;
  return {
    ...state,
    confirmed: action.snapshot,
    // A newer server snapshot supersedes this operation and every older one;
    // later local intents still replay over it if their requests remain open.
    operations: state.operations.slice(succeededIndex + 1),
    formerSlotByPlayerId: settledFormerSlots(state, succeededIndex + 1),
    status: "saved",
    message: undefined,
    failure: undefined,
    reapplyingOperationId: undefined,
    reapplyRefusal: undefined,
    savedAt: action.savedAt ?? Date.now(),
  };
};

/**
 * Retry / Discard / Reload / Re-apply — the user-driven recovery actions.
 * @param state - Current shared ordering state
 * @param action - One recovery action
 * @returns Next state
 */
const reduceRecovery = (
  state: WatchlistPreferenceOrderState,
  action: ActionOf<"retry" | "discard" | "reload" | "reapply">
): WatchlistPreferenceOrderState => {
  if (action.type === "retry") {
    if (findOperationIndex(state, action.operationId) < 0) return state;
    return { ...state, status: "saving", message: undefined };
  }
  if (action.type === "discard") {
    if (findOperationIndex(state, action.operationId) < 0) return state;
    return {
      ...state,
      operations: state.operations.filter(
        operation => operation.id !== action.operationId
      ),
      status: "idle",
      message: undefined,
      failure: undefined,
    };
  }
  if (action.type === "reload") {
    if (!state.heldChange && !state.reapplyRefusal) return state;
    return {
      ...state,
      heldChange: undefined,
      reapplyRefusal: undefined,
      status: "idle",
      message: undefined,
    };
  }
  if (!state.heldChange) return state;
  return {
    ...state,
    operations: [...state.operations, state.heldChange],
    heldChange: undefined,
    reapplyingOperationId: state.heldChange.id,
    reapplyRefusal: undefined,
    status: "saving",
    message: undefined,
  };
};

/**
 * Reduces mutation lifecycle events without coupling to Apollo or either view.
 * @param state - Current shared ordering state
 * @param action - Hydration or one operation lifecycle event
 * @returns Next immutable state
 */
export const preferenceOrderReducer = (
  state: WatchlistPreferenceOrderState,
  action: WatchlistPreferenceOrderAction
): WatchlistPreferenceOrderState => {
  if (action.type === "hydrate") {
    return reduceHydration(state, action);
  }
  if (action.watchlistId !== state.watchlistId) return state;
  if (action.type === "start") {
    return {
      ...state,
      operations: [...state.operations, action.operation],
      status: "saving",
      message: undefined,
    };
  }
  if (action.type === "failure") return reduceFailure(state, action);
  if (action.type === "conflict") return reduceConflict(state, action);
  if (action.type === "success") return reduceSuccess(state, action);
  return reduceRecovery(state, action);
};
